import React, { useCallback, useState } from "react";
import { Loader2, Volume2 } from "lucide-react";
import { motion } from "motion/react";

import { ImpressiveTooltip } from "../ui/impressive-tooltip";
import { showErrorToast } from "../../lib/toast";
import { validateUrl } from "../../lib/validation";
import { isSpeechSupported } from "../../lib/speech";
import ApiService from "../../services/api-service";
import { ArticleReaderModal } from "./ArticleReaderModal";

interface ArticleListenButtonProps {
  url?: string;
  title?: string;
}

export function ArticleListenButton({ url, title }: ArticleListenButtonProps) {
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [readyArticle, setReadyArticle] = useState<{ title?: string; content: string } | null>(null);

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (isFetching) return;

      if (!url || !validateUrl(url)) {
        showErrorToast("Invalid URL");
        return;
      }

      if (!isSpeechSupported()) {
        showErrorToast("Text-to-speech isn't supported in this browser.");
        return;
      }

      // Fetch (and implicitly check the source page's status/reachability)
      // before ever presenting the popup: a bad URL or failed download surfaces
      // as a toast, not as an opened reader with an error inside it.
      setIsFetching(true);
      try {
        const article = await ApiService.parseArticle(url);
        const content = (article?.content || "").trim();
        if (!content) {
          showErrorToast("This article contains no readable text.");
          return;
        }
        setReadyArticle({ title: article?.title || title, content });
        setIsOpen(true);
      } catch (error: any) {
        showErrorToast(error?.message || "Unable to read this article aloud. Please try again.");
      } finally {
        setIsFetching(false);
      }
    },
    [url, title, isFetching]
  );

  const label = isFetching ? "Loading…" : "Listen to Article";

  return (
    <>
      <ImpressiveTooltip
        label={label}
        description="Open a read-along view with this article spoken aloud."
        icon={<Volume2 className="w-3.5 h-3.5" />}
      >
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors disabled:opacity-60"
          onClick={handleClick}
          disabled={isFetching}
          aria-label={label}
        >
          {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
          <span className="text-xs font-medium hidden sm:inline">{label}</span>
        </motion.button>
      </ImpressiveTooltip>

      <ArticleReaderModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={readyArticle?.title}
        content={readyArticle?.content}
      />
    </>
  );
}
